import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { useAuthStore } from '../stores/authStore';
import { useActivityStore, getActiveSessionId } from '../stores/activityStore';

/**
 * アクションカテゴリの型定義
 */
export type ActionCategory =
  | 'create'
  | 'update'
  | 'delete'
  | 'export'
  | 'import'
  | 'download'
  | 'search'
  | 'filter'
  | 'navigate'
  | 'other';

/**
 * 活動追跡設定
 */
interface ActivityTrackerConfig {
  // ハートビート間隔（ミリ秒）- デフォルト: 5分
  heartbeatInterval?: number;
  // 追跡を有効化するか
  enabled?: boolean;
  // 管理者ページ（/admin/）を除外するか
  excludeAdminPages?: boolean;
}

/**
 * ページタイトルをパスから推測
 */
const getPageTitleFromPath = (pathname: string): string => {
  const pathMap: Record<string, string> = {
    '/app/dashboard': 'ダッシュボード',
    '/app/equipments': '設備一覧',
    '/app/locations': 'エリア',
    '/app/calendar': '保全カレンダー',
    '/app/parts': '部品・消耗品',
    '/app/suppliers': '仕入先',
    '/app/templates': '作業テンプレート',
    '/app/settings': '設定',
    '/app/history': '作業履歴',
    '/app/export': 'エクスポート',
    '/app/import': 'インポート',
  };

  // 完全一致
  if (pathMap[pathname]) {
    return pathMap[pathname];
  }

  // プレフィックス一致
  for (const [prefix, title] of Object.entries(pathMap)) {
    if (pathname.startsWith(prefix)) {
      return title;
    }
  }

  // 詳細ページのパターン
  if (pathname.includes('/equipments/')) {
    return '設備詳細';
  }
  if (pathname.includes('/locations/')) {
    return 'エリア詳細';
  }
  if (pathname.includes('/schedules/')) {
    return '作業予定詳細';
  }
  if (pathname.includes('/parts/')) {
    return '部品詳細';
  }
  if (pathname.includes('/templates/')) {
    return 'テンプレート詳細';
  }

  return pathname;
};

/**
 * ユーザー活動を追跡するHook
 */
export function useActivityTracker(config: ActivityTrackerConfig = {}) {
  const {
    heartbeatInterval = 5 * 60 * 1000, // 5分
    enabled = true,
    excludeAdminPages = true,
  } = config;

  const location = useLocation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const sessionId = useActivityStore((state) => state.sessionId);

  // tRPC mutations
  const startSessionMutation = trpc.userActivity.startSession.useMutation();
  const endSessionMutation = trpc.userActivity.endSession.useMutation();
  const heartbeatMutation = trpc.userActivity.heartbeat.useMutation();
  const trackPageViewMutation = trpc.userActivity.trackPageView.useMutation();
  const trackActionMutation = trpc.userActivity.trackAction.useMutation();

  // refs
  const heartbeatTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializedRef = useRef(false);
  const previousAuthStateRef = useRef(isAuthenticated);
  const lastTrackedPathRef = useRef<string | null>(null);
  const isProcessingRef = useRef(false);

  /**
   * 管理者ページかどうかをチェック
   */
  const isAdminPage = useCallback(
    (pathname: string): boolean => {
      return excludeAdminPages && pathname.startsWith('/admin');
    },
    [excludeAdminPages]
  );

  /**
   * セッションを開始
   */
  const initializeSession = useCallback(async () => {
    if (!enabled || !isAuthenticated || isProcessingRef.current) return;

    // 既存のセッションがあれば再利用
    const existingSessionId = getActiveSessionId();
    if (existingSessionId) {
      return;
    }

    isProcessingRef.current = true;
    try {
      const result = await startSessionMutation.mutateAsync();
      if (result.success && result.sessionId) {
        useActivityStore.getState().setSessionId(result.sessionId);
      }
    } catch (error) {
      console.warn('[ActivityTracker] Failed to start session:', error);
    } finally {
      isProcessingRef.current = false;
    }
  }, [enabled, isAuthenticated, startSessionMutation]);

  /**
   * セッションを終了
   */
  const terminateSession = useCallback(async () => {
    const currentSessionId = getActiveSessionId();
    if (!currentSessionId) return;

    try {
      await endSessionMutation.mutateAsync({ sessionId: currentSessionId });
    } catch (error) {
      console.warn('[ActivityTracker] Failed to end session:', error);
    } finally {
      useActivityStore.getState().clearSession();
    }
  }, [endSessionMutation]);

  /**
   * ハートビートを送信
   */
  const sendHeartbeat = useCallback(async () => {
    const currentSessionId = getActiveSessionId();
    if (!currentSessionId) return;

    try {
      await heartbeatMutation.mutateAsync({ sessionId: currentSessionId });
    } catch (error) {
      console.warn('[ActivityTracker] Failed to send heartbeat:', error);
    }
  }, [heartbeatMutation]);

  /**
   * ページビューを記録
   */
  const trackPageView = useCallback(
    async (pathname: string) => {
      const currentSessionId = getActiveSessionId();
      if (!currentSessionId || isAdminPage(pathname)) return;

      const pageTitle = getPageTitleFromPath(pathname);
      const state = useActivityStore.getState();
      const referrerPath = state.lastPagePath ?? undefined;

      try {
        await trackPageViewMutation.mutateAsync({
          sessionId: currentSessionId,
          pagePath: pathname,
          pageTitle,
          referrerPath,
        });
        state.updateLastPage(pathname);
      } catch (error) {
        console.warn('[ActivityTracker] Failed to track page view:', error);
      }
    },
    [isAdminPage, trackPageViewMutation]
  );

  /**
   * アクションを記録（外部から呼び出し可能）
   */
  const trackAction = useCallback(
    async (
      actionCategory: ActionCategory,
      actionTarget: string,
      actionDetail?: Record<string, unknown>
    ) => {
      const currentSessionId = getActiveSessionId();
      if (!currentSessionId) return;

      if (isAdminPage(location.pathname)) return;

      try {
        await trackActionMutation.mutateAsync({
          sessionId: currentSessionId,
          actionCategory,
          actionTarget,
          actionDetail,
          pagePath: location.pathname,
        });
      } catch (error) {
        console.warn('[ActivityTracker] Failed to track action:', error);
      }
    },
    [isAdminPage, location.pathname, trackActionMutation]
  );

  // ============================================================================
  // Effects
  // ============================================================================

  /**
   * 認証状態の変化を監視
   */
  useEffect(() => {
    if (!enabled) return;

    const wasAuthenticated = previousAuthStateRef.current;

    // ログイン: false -> true
    if (!wasAuthenticated && isAuthenticated) {
      void initializeSession();
    }

    // ログアウト: true -> false
    if (wasAuthenticated && !isAuthenticated) {
      void terminateSession();
    }

    previousAuthStateRef.current = isAuthenticated;
  }, [enabled, isAuthenticated, initializeSession, terminateSession]);

  /**
   * 初期化: ページロード時にセッションを確認
   */
  useEffect(() => {
    if (!enabled || isInitializedRef.current) return;
    isInitializedRef.current = true;

    if (isAuthenticated) {
      void initializeSession();
    }
  }, [enabled, isAuthenticated, initializeSession]);

  /**
   * ページ遷移時にページビューを記録
   */
  useEffect(() => {
    if (!enabled || !isAuthenticated) return;

    // セッションがまだない場合は待機
    const currentSessionId = getActiveSessionId();
    if (!currentSessionId) return;

    // 同じパスの場合はスキップ（無限ループ防止）
    if (lastTrackedPathRef.current === location.pathname) return;
    lastTrackedPathRef.current = location.pathname;

    void trackPageView(location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isAuthenticated, location.pathname, sessionId]);

  /**
   * ハートビートタイマー
   */
  useEffect(() => {
    const currentSessionId = getActiveSessionId();
    if (!enabled || !isAuthenticated || !currentSessionId) {
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
      return;
    }

    heartbeatTimerRef.current = setInterval(() => {
      void sendHeartbeat();
    }, heartbeatInterval);

    return () => {
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
    };
  }, [enabled, isAuthenticated, heartbeatInterval, sendHeartbeat, sessionId]);

  /**
   * ページを離れる前にセッション終了
   */
  useEffect(() => {
    if (!enabled) return;

    const handleBeforeUnload = () => {
      // ローカルストレージのセッションはクリアしない
      // （タブを閉じてもセッションは継続、8時間で自動失効）
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled]);

  return {
    sessionId,
    trackAction,
    trackPageView,
  };
}

/**
 * アクション追跡用のヘルパーHook
 */
export function useTrackAction() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const location = useLocation();
  const trackActionMutation = trpc.userActivity.trackAction.useMutation();

  return useCallback(
    async (
      actionCategory: ActionCategory,
      actionTarget: string,
      actionDetail?: Record<string, unknown>
    ) => {
      const currentSessionId = getActiveSessionId();
      if (!currentSessionId || !isAuthenticated) return;

      // 管理者ページは除外
      if (location.pathname.startsWith('/admin')) return;

      try {
        await trackActionMutation.mutateAsync({
          sessionId: currentSessionId,
          actionCategory,
          actionTarget,
          actionDetail,
          pagePath: location.pathname,
        });
      } catch (error) {
        console.warn('[TrackAction] Failed to track action:', error);
      }
    },
    [isAuthenticated, location.pathname, trackActionMutation]
  );
}
